/**
 * AVOS mark — the OFFICIAL master artwork (AVOS Brand Guidelines §02).
 *
 * Two overlapping domes on a shared baseline: the outer arches are the dark
 * navy mark colour (#10233C) and the inner intersection arch is the AVOS brand
 * light indigo (#97A6ED). This is the supplied master SVG, reproduced verbatim
 * (never redrawn).
 *
 * Rendering notes:
 *   - `size` sets the rendered height in px; the width derives from the artwork
 *     aspect ratio (79 × 28).
 *   - `baselineColor` overrides the colour of the dark outer arches so the mark
 *     stays legible on any chrome (default: the master `#10233C`). The indigo
 *     intersection arch is always preserved as the brand accent.
 *
 * Decorative here (the wordmark carries the accessible name), so aria-hidden;
 * callers provide the accessible label on the lockup.
 */
import { type ReactElement } from "react";

/** The dark navy of the outer arches in the master artwork. */
const ARCH_NAVY = "#10233C";
/** The brand indigo of the inner intersection arch (never overridden). */
const ARCH_INDIGO = "#97A6ED";
/** Master artwork aspect ratio (viewBox 79 × 28). */
const ASPECT = 79 / 28;

/** Props for {@link ArchMark}. */
export interface ArchMarkProps {
  /** Rendered height in px (width derives from the 79:28 artwork ratio). */
  readonly size?: number;
  /**
   * Colour for the dark outer arches. Defaults to the master `#10233C`; pass a
   * light colour to keep the mark legible on dark chrome. The indigo
   * intersection arch is unaffected.
   */
  readonly baselineColor?: string;
}

/**
 * Render the official AVOS mark as an inline SVG at the given height.
 */
export function ArchMark({
  size = 26,
  baselineColor = ARCH_NAVY,
}: ArchMarkProps): ReactElement {
  const width = Math.round(size * ASPECT);

  return (
    <svg
      className="console-sidebar__mark"
      width={width}
      height={size}
      viewBox="0 0 79 28"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-hidden="true"
      focusable="false"
    >
      {/* Outer domes + shared baseline (dark navy, overridable for dark chrome). */}
      <path
        d="M38.8451 2.97989C45.1589 -0.25416 52.5043 -0.82841 59.2441 1.38519C65.7773 3.46196 71.5204 8.39216 74.6545 14.4771C76.1853 17.3896 77.1486 20.5665 77.4923 23.8386C77.6003 24.7823 77.5936 25.9502 77.7202 26.8155C76.8405 26.7779 75.7842 26.7235 74.9171 26.8243C75.026 20.8199 72.4399 14.5335 68.2874 10.2235C63.9274 5.63057 57.9172 2.96325 51.5851 2.81165C47.6497 2.73923 45.2909 3.31381 41.649 4.61864C47.2101 8.22274 51.4552 14.5036 52.8425 20.9732C53.1046 22.1855 53.2863 23.4136 53.3862 24.6497C53.4409 25.3175 53.4564 26.1927 53.5369 26.8157C52.5709 26.7549 51.6742 26.7735 50.709 26.7866L26.9052 26.8143C25.9868 26.7828 25.0806 26.7492 24.1622 26.823C24.2544 25.4276 24.2869 24.2573 24.5018 22.863C25.4253 16.5342 28.5985 10.7498 33.4402 6.56929C34.1426 5.95512 35.254 5.09919 36.0541 4.58893C34.3024 4.01079 32.8764 3.5142 31.0561 3.17023C25.9056 2.24213 20.5928 3.01863 15.9235 5.38197C8.0811 9.33469 2.50738 17.9469 2.82476 26.8183C1.95266 26.7407 0.876322 26.748 0 26.8146C0.179841 19.337 2.81522 12.6884 8.26854 7.4768C13.3682 2.58112 20.209 -0.0811168 27.2772 0.0791799C30.9846 0.167474 35.591 1.12934 38.8451 2.97989Z"
        fill={baselineColor}
      />
      {/* The inner intersection arch — AVOS brand indigo (always preserved). */}
      <path
        d="M27.6489 20.9643C29.241 14.379 33.2342 9.61731 38.8548 6.03711C44.9652 9.98434 48.6264 14.6438 50.263 21.9011C50.4266 22.6271 50.9443 26.3745 50.7082 26.7866L26.9043 26.8145C26.9051 25.6296 27.0581 23.6812 27.3053 22.567C27.3902 21.959 27.494 21.5582 27.6489 20.9643Z"
        fill={ARCH_INDIGO}
      />
    </svg>
  );
}

export default ArchMark;
